import React from 'react';
import { SectionCard, TextField, TextAreaField, ImageField, Repeater } from '../../../components/admin/EditorFields';

const ICONS = [
  'Heart', 'HandHeart', 'Users', 'Building2', 'Wallet', 'Wrench',
  'Megaphone', 'GraduationCap', 'MapPin', 'Mail', 'Phone', 'Sparkles',
  'ShieldCheck', 'FileText', 'Euro', 'Handshake', 'Globe2', 'Zap',
  'ClipboardCheck', 'Rocket', 'Check', 'Leaf', 'Droplet',
];

function IconSelect({ value, onChange, label = 'Icône' }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">{label}</label>
      <select
        value={value || 'Sparkles'}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-11 rounded-xl border border-sky-200 px-3 text-sm"
      >
        {ICONS.map((i) => <option key={i} value={i}>{i}</option>)}
      </select>
    </div>
  );
}

export default function SupportEditor({ data, setData }) {
  const upd = (path, value) => setData((d) => {
    const next = JSON.parse(JSON.stringify(d || {}));
    const keys = path.split('.');
    let cur = next;
    for (let i = 0; i < keys.length - 1; i++) {
      cur[keys[i]] = cur[keys[i]] || {};
      cur = cur[keys[i]];
    }
    cur[keys[keys.length - 1]] = value;
    return next;
  });

  return (
    <div className="space-y-5">
      {/* HERO */}
      <SectionCard title="Hero (en-tête)" description="La bannière principale de la page.">
        <TextField label="Eyebrow (au-dessus du titre)" value={data.hero?.eyebrow} onChange={(v) => upd('hero.eyebrow', v)} />
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="Titre" value={data.hero?.title} onChange={(v) => upd('hero.title', v)} />
          <TextField label="Titre en couleur" value={data.hero?.titleHighlight} onChange={(v) => upd('hero.titleHighlight', v)} />
        </div>
        <TextAreaField label="Description" rows={4} value={data.hero?.description} onChange={(v) => upd('hero.description', v)} />
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="Bouton principal - libellé" value={data.hero?.ctaLabel} onChange={(v) => upd('hero.ctaLabel', v)} />
          <TextField label="Bouton secondaire - libellé" value={data.hero?.ctaSecondaryLabel} onChange={(v) => upd('hero.ctaSecondaryLabel', v)} />
        </div>
        <ImageField label="Image du hero (mosaïque de portraits)" value={data.hero?.image} onChange={(v) => upd('hero.image', v)} />
      </SectionCard>

      {/* BESOINS FINANCIERS */}
      <SectionCard title="Des besoins financiers" description="Détails du coût d'un kiosque et frais de fonctionnement.">
        <TextAreaField label="Introduction (avec les chiffres 42 000€ / 21 000€)" rows={3} value={data.financial?.intro} onChange={(v) => upd('financial.intro', v)} />

        <Repeater
          label="+ 4 000 € consacrés à…"
          items={data.financial?.extra}
          onChange={(items) => upd('financial.extra', items)}
          newItem={() => ''}
          renderItem={(item, update, i) => (
            <TextAreaField
              label={`Item ${i + 1}`}
              rows={2}
              value={item}
              onChange={(v) => setData((d) => {
                const next = JSON.parse(JSON.stringify(d));
                next.financial.extra[i] = v;
                return next;
              })}
            />
          )}
        />

        <Repeater
          label="Frais de fonctionnement"
          items={data.financial?.operatingCosts}
          onChange={(items) => upd('financial.operatingCosts', items)}
          newItem={() => ''}
          renderItem={(item, update, i) => (
            <TextField
              label={`Ligne ${i + 1}`}
              value={item}
              onChange={(v) => setData((d) => {
                const next = JSON.parse(JSON.stringify(d));
                next.financial.operatingCosts[i] = v;
                return next;
              })}
            />
          )}
        />

        <TextAreaField label="Note finale (encart vert)" rows={3} value={data.financial?.note} onChange={(v) => upd('financial.note', v)} />
      </SectionCard>

      {/* BESOINS HUMAINS - LOCAL */}
      <SectionCard title="Des besoins humains — Localement" description="Cercles de citoyens et actions locales.">
        <TextAreaField label="Paragraphe 1 — Cercles de citoyens" rows={4} value={data.human?.local1} onChange={(v) => upd('human.local1', v)} />
        <TextAreaField label="Paragraphe 2 — Défi d'accompagnement" rows={4} value={data.human?.local2} onChange={(v) => upd('human.local2', v)} />
        <TextAreaField label="Encart bleu — Plateformes de bénévolat" rows={2} value={data.human?.local3} onChange={(v) => upd('human.local3', v)} />
        <ImageField label="Photo — Bénévoles / Dégustation" value={data.human?.image} onChange={(v) => upd('human.image', v)} />
        <TextField
          label="URL du PDF à télécharger (bouton « En savoir plus »)"
          value={data.human?.localPdfUrl}
          onChange={(v) => upd('human.localPdfUrl', v)}
          hint="Colle l'URL complète du PDF (https://…). Laisse vide pour masquer le bouton."
        />
        <TextField
          label="Libellé du bouton PDF"
          value={data.human?.localPdfLabel}
          onChange={(v) => upd('human.localPdfLabel', v)}
          hint="Par défaut : « En savoir plus »"
        />
      </SectionCard>

      {/* BESOINS HUMAINS - NATIONAL */}
      <SectionCard title="À l'échelle nationale" description="Missions réalisables à distance.">
        <Repeater
          label="Missions à distance"
          items={data.national?.missions}
          onChange={(items) => upd('national.missions', items)}
          newItem={() => ({ icon: 'Sparkles', label: '' })}
          renderItem={(item, update) => (
            <div className="grid sm:grid-cols-2 gap-3">
              <IconSelect value={item.icon} onChange={(v) => update({ icon: v })} />
              <TextField label="Libellé" value={item.label} onChange={(v) => update({ label: v })} />
            </div>
          )}
        />
      </SectionCard>

      {/* DÉPLOIEMENT NATIONAL */}
      <SectionCard title="Développer des Kiosques à Eau partout en France">
        <TextAreaField label="Introduction" rows={3} value={data.deploy?.intro} onChange={(v) => upd('deploy.intro', v)} />
        <TextField label="Question CTA (bandeau bleu)" value={data.deploy?.question} onChange={(v) => upd('deploy.question', v)} />

        <Repeater
          label="Nous vous accompagnons à chaque étape"
          items={data.deploy?.help}
          onChange={(items) => upd('deploy.help', items)}
          newItem={() => ''}
          renderItem={(item, update, i) => (
            <TextField
              label={`Étape ${i + 1}`}
              value={item}
              onChange={(v) => setData((d) => {
                const next = JSON.parse(JSON.stringify(d));
                next.deploy.help[i] = v;
                return next;
              })}
            />
          )}
        />

        <Repeater
          label="Types de porteurs de projet"
          items={data.deploy?.bearers}
          onChange={(items) => upd('deploy.bearers', items)}
          newItem={() => ({ icon: 'Users', label: '' })}
          renderItem={(item, update) => (
            <div className="grid sm:grid-cols-2 gap-3">
              <IconSelect value={item.icon} onChange={(v) => update({ icon: v })} />
              <TextField label="Libellé" value={item.label} onChange={(v) => update({ label: v })} />
            </div>
          )}
        />
      </SectionCard>

      {/* RÉUSSIR GRÂCE À VOTRE DON */}
      <SectionCard title="Réussir grâce à votre don" description="Bloc 'passer à l'action' avec les 3 raisons + particulier + entreprise.">
        <TextAreaField label="Texte introductif (au-dessus des 3 raisons)" rows={3} value={data.success?.text1} onChange={(v) => upd('success.text1', v)} />

        <Repeater
          label="3 raisons de faire un don"
          items={data.success?.reasons}
          onChange={(items) => upd('success.reasons', items)}
          newItem={() => ({ icon: 'Sparkles', text: '' })}
          renderItem={(item, update) => (
            <div className="space-y-2">
              <IconSelect value={item.icon} onChange={(v) => update({ icon: v })} />
              <TextAreaField label="Texte" rows={2} value={item.text} onChange={(v) => update({ text: v })} />
            </div>
          )}
        />

        <TextField label="Phrase de remerciement (sous les 3 raisons)" value={data.success?.thankYou} onChange={(v) => upd('success.thankYou', v)} />

        {/* PARTICULIER */}
        <div className="p-4 rounded-2xl bg-sky-50/60 border border-sky-100 space-y-3">
          <p className="text-xs font-bold uppercase text-slate-600 tracking-wider">Encart « Vous êtes un particulier »</p>
          <TextAreaField label="Texte d'introduction" rows={2} value={data.success?.particulier?.intro} onChange={(v) => upd('success.particulier.intro', v)} />
          <div className="grid sm:grid-cols-3 gap-3">
            <TextField label="Exemple - Don" value={data.success?.particulier?.exampleDonation} onChange={(v) => upd('success.particulier.exampleDonation', v)} />
            <TextField label="Exemple - Réduction" value={data.success?.particulier?.exampleReduction} onChange={(v) => upd('success.particulier.exampleReduction', v)} />
            <TextField label="Exemple - Coût réel" value={data.success?.particulier?.exampleReal} onChange={(v) => upd('success.particulier.exampleReal', v)} />
          </div>
          <TextAreaField label="Note (limite 20% du revenu…)" rows={2} value={data.success?.particulier?.note} onChange={(v) => upd('success.particulier.note', v)} />
          <TextAreaField label="Mention reçu fiscal" rows={2} value={data.success?.particulier?.receipt} onChange={(v) => upd('success.particulier.receipt', v)} />
        </div>

        {/* ENTREPRISE */}
        <div className="p-4 rounded-2xl bg-amber-50/60 border border-amber-100 space-y-3">
          <p className="text-xs font-bold uppercase text-slate-600 tracking-wider">Encart « Vous êtes une entreprise »</p>
          <TextAreaField label="Texte d'introduction (article 238 bis…)" rows={3} value={data.success?.entreprise?.intro} onChange={(v) => upd('success.entreprise.intro', v)} />
          <div className="grid sm:grid-cols-2 gap-3">
            <TextField label="Exemple - Don" value={data.success?.entreprise?.exampleDonation} onChange={(v) => upd('success.entreprise.exampleDonation', v)} />
            <TextField label="Exemple - Réduction" value={data.success?.entreprise?.exampleReduction} onChange={(v) => upd('success.entreprise.exampleReduction', v)} />
          </div>
          <TextField label="Mention reçu fiscal" value={data.success?.entreprise?.receipt} onChange={(v) => upd('success.entreprise.receipt', v)} />
          <TextAreaField label="Encart bas — Installation Kiosque + mécénat compétences" rows={4} value={data.success?.entreprise?.extra} onChange={(v) => upd('success.entreprise.extra', v)} />
        </div>

        <TextAreaField label="Texte final avant CTA (bloc sombre)" rows={3} value={data.success?.text2} onChange={(v) => upd('success.text2', v)} />
        <TextField label="URL de l'agrément fiscal (PDF)" hint="Lien vers le document officiel" value={data.success?.fiscalPdf} onChange={(v) => upd('success.fiscalPdf', v)} />
      </SectionCard>
    </div>
  );
}
